import React, { useEffect, useState } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Search, Inject, Toolbar } from '@syncfusion/ej2-react-grids';
import Swal from 'sweetalert2';
import { Header } from '../components';
import { MdDeleteForever, MdEditNote, MdOutlinePeople } from "react-icons/md";

const Members = () => {
  const [membersData, setMembersData] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentEditMember, setCurrentEditMember] = useState(null);
  const [newMember, setNewMember] = useState({
    first_name: '',
    sur_name: '',
    title: '',
    dob: '',
    phone: '',
    email: '',
    address: '',
    gender: '',
    vaaar_id: '',
    adhaar_id: '',
    image_url: '', // For existing image
    image: null,   // For new image file
    _id: null,     // For member ID during edit mode
  });


  useEffect(() => {
    fetchMembers();
  }, []);

  // Function to fetch members data from the API  
  const fetchMembers = async () => {
    try {
      if (!process.env.REACT_APP_API_BASE_URL || !process.env.REACT_APP_API_KEY) {
        throw new Error('API configuration missing');
      }
      const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/members', {
        headers: {
          'api-key': process.env.REACT_APP_API_KEY,
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Invalid data format received');
      }

      const reversedData = [...data].reverse();
      setMembersData(reversedData);
    } catch (error) {
      console.error('Error fetching members:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to fetch members',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };


  const handleEditMember = (member) => {
    setIsEditMode(true);
    setShowAddForm(true);

    setNewMember({
      first_name: member.first_name,
      sur_name: member.sur_name,
      title: member.title,
      join_date: member.join_date,
      dob: member.dob,
      phone: member.phone,
      adhaar_id: member.adhaar_id,
      email: member.email,
      address: member.address,
      gender: member.gender,
      vaaar_id: member.vaaar_id,
      image_url: member.image, // Existing image URL
      _id: member._id, // Member ID
    });

    setCurrentEditMember(member);
  };

  const handleAddMember = async (e) => {
    e.preventDefault();

    try {
      // Validate required fields
      if (
        !newMember.first_name?.trim() ||
        !newMember.sur_name?.trim() ||
        !newMember.title?.trim() ||
        !newMember.join_date?.trim() ||
        !newMember.dob ||
        !newMember.phone?.trim() ||
        !newMember.adhaar_id?.trim() ||
        !newMember.email?.trim() ||
        !newMember.address?.trim() ||
        !newMember.gender?.trim() ||
        !newMember.vaaar_id?.trim() ||
        (!newMember.image && !isEditMode)
      ) {
        await Swal.fire({
          title: 'Error!',
          text: 'All fields are required',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#d33',
        });
        return;
      }

      // Upload new image if applicable
      let uploadedImageName = newMember.image_url;

      if (isEditMode && newMember.image && newMember.image_url) {
        const imageFilename = formatImageFilename(newMember.image_url);
        // Delete the old image
        const deleteResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/upload/${imageFilename}`,
          {
            method: 'DELETE',
            headers: {
              'api-key': process.env.REACT_APP_API_KEY,
            },
          }
        );

        if (!deleteResponse.ok) {
          const errorData = await deleteResponse.json().catch(() => null);
          throw new Error(errorData?.message || 'Failed to delete previous image');
        }
      }

      if (newMember.image) {
        // Upload new image
        const imageFile = newMember.image;
        const formData = new FormData();
        formData.append('file', imageFile);

        const uploadResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => null);
          throw new Error(errorData?.message || 'Failed to upload image');
        }

        const uploadResult = await uploadResponse.json();
        uploadedImageName = uploadResult.fileUrl; // Use the returned filename
      }

      // Prepare member data
      const memberData = {
        first_name: newMember.first_name,
        sur_name: newMember.sur_name,
        title: newMember.title,
        join_date: newMember.join_date,
        dob: newMember.dob,
        phone: newMember.phone,
        adhaar_id: newMember.adhaar_id,
        email: newMember.email,
        address: newMember.address,
        gender: newMember.gender,
        vaaar_id: newMember.vaaar_id,
        image: uploadedImageName,
      };

      // Add or update member
      const method = isEditMode ? 'PUT' : 'POST';
      const url = isEditMode
        ? `${process.env.REACT_APP_API_BASE_URL}/api/members/${newMember._id}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/members`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'api-key': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(memberData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to add/update member');
      }

      // Success handling
      await Swal.fire({
        title: 'Success!',
        text: isEditMode ? 'Member updated successfully' : 'Member added successfully',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });

      // Reset form and refresh data
      setShowAddForm(false);
      setIsEditMode(false);
      setNewMember({
        first_name: '',
        sur_name: '',
        title: '',
        join_date: '',
        dob: '',
        phone: '',
        adhaar_id: '',
        pan_id: '',
        email: '',
        address: '',
        gender: '',
        vaaar_id: '',
        image: null,
      });

      await fetchMembers(); // Refresh members list
    } catch (error) {
      console.error('Error adding/updating member:', error);

      // Error handling
      await Swal.fire({
        title: 'Error!',
        text: error.message || 'An unexpected error occurred',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
    }
  };

  function formatImageFilename(cloudinaryUrl) {
    const regex = /\/image\/upload\/v\d+\/(uploads\/[^\/]+)(?:\.[a-z]+)$/;
    const match = cloudinaryUrl.match(regex);

    if (match && match[1]) {
      return match[1]; // Return path without file extension
    }

    return '';
  }


  const handleDelete = async (id, image) => {
    try {
      // Prompt for password
      const { value: password } = await Swal.fire({
        title: "Enter Password",
        input: "password",
        inputLabel: "Password is required to delete the member.",
        inputPlaceholder: "Enter your password",
        showCancelButton: true,
        confirmButtonText: "Submit",
        confirmButtonColor: "#d33", // Red button for submit
        cancelButtonText: "Cancel",
        cancelButtonColor: "#3085d6", // Blue button for cancel
        inputValidator: (value) => {
          if (!value) {
            return "You need to enter a password!";
          }
        },
      });
  
      // Check password
      if (!password) {
        // User canceled the dialog
        return;
      }
  
      if (password !== "8828") {
        await Swal.fire({
          icon: "error",
          title: "Incorrect Password",
          text: "The password you entered is incorrect.",
          confirmButtonText: "Okay",
          confirmButtonColor: "#4caf50", // Green button
        });
        return; // Stop further execution if the password is incorrect
      }
  
      // Confirm deletion
      const confirmResult = await Swal.fire({
        title: "Are you sure?",
        text: "You won’t be able to undo this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        confirmButtonColor: "#d33",
        cancelButtonText: "Cancel",
      });
  
      if (!confirmResult.isConfirmed) return;
  
      // Delete image if available
      if (image) {
        const imageFilename = formatImageFilename(image);
        const deleteImageResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/upload/${imageFilename}`,
          {
            method: "DELETE",
            headers: {
              "api-key": process.env.REACT_APP_API_KEY,
            },
          }
        );
  
        if (!deleteImageResponse.ok) {
          const errorData = await deleteImageResponse.json().catch(() => null);
          throw new Error(errorData?.message || "Failed to delete member image");
        }
      }
  
      // Delete member
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/members/${id}`,
        {
          method: "DELETE",
          headers: {
            "api-key": process.env.REACT_APP_API_KEY,
          },
        }
      );
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || "Failed to delete member");
      }
  
      // Success handling
      await Swal.fire({
        title: "Deleted!",
        text: "Member has been deleted.",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6", // Blue button
      });
  
      await fetchMembers(); // Refresh members list
    } catch (error) {
      console.error("Error deleting member:", error);
  
      // Error handling
      await Swal.fire({
        title: "Error!",
        text: error.message || "An unexpected error occurred",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#d33", // Red button
      });
    }
  };
  

  const membersGrid = [
    {
      headerText: 'Member',
      width: '150',
      textAlign: 'Center',
      template: (props) => (
        <div className="flex items-center gap-2">
          <img
            className="rounded-full w-10 h-10"
            src={props.image}
            alt="employee"
          />
          <p>{props.first_name} {props.sur_name}</p>
        </div>
      )
    },
    {
      field: 'first_name',
      headerText: '',
      width: '0',
      textAlign: 'Center',
    },
    {
      field: 'sur_name',
      headerText: '',
      width: '0',
      textAlign: 'Center',
    },
    {
      field: 'title',
      headerText: 'Designation',
      width: '170',
      textAlign: 'Center',
    },
    // { field: 'join_date',

    //   headerText: 'Join Date',
    //   width: '120',
    //   format: 'yMd',
    //   textAlign: 'Center' },
    {
      field: 'vaaar_id',
      headerText: 'Vaaar ID',
      width: '125',
      textAlign: 'Center'
    },

    {
      field: 'phone',
      headerText: 'Phone',
      width: '120',
      textAlign: 'Center'
    },
    {
      field: 'email',
      headerText: 'Email',
      width: '125',
      textAlign: 'Center'
    },
    {
      field: 'gender',
      headerText: 'Gender',
      width: '120',
      textAlign: 'Center'
    },
    {
      field: 'dob',
      headerText: 'DOB',
      width: '120',
      textAlign: 'Center'
    },
    {
      field: '_id',
      headerText: 'Actions',
      width: '150',
      textAlign: 'Center',
      template: (props) => (
        <div>
          <a href={`/members/profiles/${props._id}`}>
            <button className="bg-orange-400 text-white px-1 py-1 text-xl rounded mr-2">
              <MdOutlinePeople />
            </button>
          </a>
          <button
            className="bg-blue-500 text-white px-1 py-1 text-xl rounded mr-2"
            onClick={() => handleEditMember(props)}
          >
            <MdEditNote />
          </button>
          <button
            className="bg-red-500 text-white px-1 py-1 text-xl rounded"
            onClick={() => {
              Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!',
              }).then(async (result) => {
                if (result.isConfirmed) {
                  try {
                    await handleDelete(props._id, props.image);

                    Swal.fire({
                      title: 'Deleted!',
                      text: 'Member has been deleted.',
                      icon: 'success',
                      confirmButtonText: 'OK',
                      confirmButtonColor: '#3085d6',
                    });
                  } catch (error) {
                    Swal.fire({
                      title: 'Error!',
                      text: 'Failed to delete the Member or associated file.',
                      icon: 'error',
                      confirmButtonText: 'OK',
                      confirmButtonColor: '#3085d6',
                    });
                  }
                }
              });
            }}
          >
            <MdDeleteForever />
          </button>
        </div>
      )
    },
  ];

  return (
    <div className="m-2 md:m-10 sm:mt-24 p-2 md:p-4 bg-white rounded-3xl">
      <div className="flex justify-between items-center mb-4">
        <Header category="Page" title="Members" />
        <button
          onClick={() => {
            setShowAddForm(true);
            setIsEditMode(false);
            setNewMember({
              first_name: '',
              sur_name: '',
              title: '',
              join_date: '',
              dob: '',
              phone: '',
              email: '',
              address: '',
              gender: '',
              vaaar_id: '',
              adhaar_id: '',
              image_url: '',
              image: null,
              _id: null,
            });
          }}
          className="bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600 transition duration-200"
        >
          Add Member
        </button>
      </div>



      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white p-8 rounded-xl shadow-2xl w-[600px] h-4/5 overflow-y-auto">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800 border-b pb-2">
              {isEditMode ? 'Edit Member' : 'Add New Member'}
            </h2>
            <form onSubmit={handleAddMember} className="space-y-6">
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">First Name:</label>
                <input
                  type="text"
                  value={newMember.first_name}
                  onChange={(e) => setNewMember({ ...newMember, first_name: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                  placeholder="Enter First Name"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Surname:</label>
                <input
                  type="text"
                  value={newMember.sur_name}
                  onChange={(e) => setNewMember({ ...newMember, sur_name: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                  placeholder="Enter Surname"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Title:</label>
                <input
                  type="text"
                  value={newMember.title}
                  onChange={(e) => setNewMember({ ...newMember, title: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                  placeholder="Enter Title"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Date of Join:</label>
                <input
                  type="date"
                  value={newMember.join_date ? new Date(newMember.join_date).toISOString().split('T')[0] : ''}
                  onChange={(e) => setNewMember({ ...newMember, join_date: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Date of Birth:</label>
                <input
                  type="date"
                  value={newMember.dob ? new Date(newMember.dob).toISOString().split('T')[0] : ''}
                  onChange={(e) => setNewMember({ ...newMember, dob: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Phone:</label>
                <input
                  type="text"
                  value={newMember.phone}
                  onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                  placeholder="Enter Phone Number"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Adhaar No:</label>
                <input
                  type="text"
                  value={newMember.adhaar_id}
                  onChange={(e) => setNewMember({ ...newMember, adhaar_id: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                  placeholder="Enter Adhaar Number"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Email:</label>
                <input
                  type="email"
                  value={newMember.email}
                  onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                  placeholder="Enter Email Address"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Vaaar ID:</label>
                <input
                  type="text"
                  value={newMember.vaaar_id}
                  onChange={(e) => setNewMember({ ...newMember, vaaar_id: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                  placeholder="Enter Vaaar ID"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">Gender:</label>
                <select
                  value={newMember.gender}
                  onChange={(e) => setNewMember({ ...newMember, gender: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  required
                >
                  <option value="" disabled>Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">Address:</label>
                <textarea
                  value={newMember.address}
                  onChange={(e) => setNewMember({ ...newMember, address: e.target.value })}
                  className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                  rows="3"
                  required
                ></textarea>
              </div>
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2">Upload Image:</label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      const file = e.target.files[0];

                      // Validate file size (max 400KB)
                      if (file.size > 400 * 1024) {
                        Swal.fire({
                          icon: "error",
                          title: "File Too Large",
                          text: "The selected file must be less than 400KB.",
                          confirmButtonText: "Okay",
                          confirmButtonColor: '#3085d6',
                        });
                        e.target.value = ""; // Reset the input field
                        return;
                      }

                      setNewMember({ ...newMember, image: file });
                    }
                  }}
                  className="border border-gray-300 p-2 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />

                {isEditMode && (
                  <div className="text-sm text-gray-600 mt-2">
                    <p>Current Image:</p>
                    <img
                      src={newMember.image_url}
                      alt="Current Event"
                      className="mt-2 w-32 h-32 object-cover border"
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setIsEditMode(false);
                    setNewMember({
                      first_name: '',
                      sur_name: '',
                      title: '',
                      join_date: '',
                      dob: '',
                      phone: '',
                      adhaar_id: '',
                      email: '',
                      address: '',
                      gender: '',
                      vaaar_id: '',
                      image: null,
                    });
                  }}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg transition duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg transition duration-200 font-medium"
                >
                  {isEditMode ? 'Update Member' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>

      )}


      <GridComponent
        dataSource={membersData}
        allowPaging
        allowSorting
        toolbar={['Search']}

      >
        <ColumnsDirective>
          {membersGrid.map((item, index) => (
            <ColumnDirective key={index} {...item} />
          ))}
        </ColumnsDirective>
        <Inject services={[Page, Search, Toolbar]} />
      </GridComponent>
    </div>
  )
}

export default Members